import React from "react";
import { finishLogin, getFirebaseUser } from "../../services/firebase";
import ApiService from "../../services/ApiService";
import { MESSAGES_TYPES, URL_PARAMS, USER_TYPES } from "../../assets/appConstants";
import { withCustomToken } from "./patientOnboardingCompletion";
import { postMessage, resolveAppTypeHintFromUrlParams } from "../../services/utils";
import OnboardingWizardModal from "./onboarding-wizard-modal";
import ClinicianOnboardingWizardModal from "../clinicianOnBoarding/clinician-onboarding-wizard-modal";
import SpinnerMan from "../common/spinner-man";
import SystemPromptService from "../../services/systemPromptService";
import { ALERTS } from "../common/systemAlert";
import {
  bootstrapClinicianRegistration,
  exchangeOtlAndSignIn,
  exchangeRegistrationCodeAndSignIn,
  handlePostOtlAuth,
  isClinicianAccountType,
  resolveAccountType,
} from "../therapist/registrationWizard/registrationWizardInviteFlow";

class OnboardingRouter extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      route: null,
      prefetchedRegistration: null,
      prefetchedLoginRes: null,
      prefetchedPatientAccount: null,
      accountId: null,
    };
  }

  componentDidMount() {
    const urlParams = this.props.urlParams || {};
    const registrationCode = urlParams[URL_PARAMS.REGISTRATION_CODE];
    const otlCode = urlParams[URL_PARAMS.OTL];

    if (registrationCode) {
      exchangeRegistrationCodeAndSignIn(registrationCode)
        .then((tokenRes) => {
          if (!tokenRes?.customToken) {
            throw new Error("Registration code exchange failed");
          }
          return this.initOnboarding(tokenRes.customToken, urlParams, {
            clinicianRegistrationOnly: true,
            alreadySignedIn: true,
          });
        })
        .catch(() => {
          postMessage(MESSAGES_TYPES.ONBOARDING_END_UNSUCCESSFULLY);
          SystemPromptService.showAlert(ALERTS.INVALID_OTL_CODE);
        });
      return;
    }

    if (otlCode) {
      exchangeOtlAndSignIn(otlCode)
        .then((tokenRes) => {
          if (!tokenRes?.customToken) {
            throw new Error("OTL exchange failed");
          }
          return handlePostOtlAuth(tokenRes, urlParams).then((route) => ({
            route,
            customToken: tokenRes.customToken,
          }));
        })
        .then(({ route, customToken }) => {
          if (route.action === "finishLogin") {
            finishLogin(route.loginData);
            return;
          }
          if (route.action === "onboarding") {
            if (route.prefetchedRegistration) {
              postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
              this.setState({
                route: "clinician",
                prefetchedRegistration: route.prefetchedRegistration,
                accountId: route.prefetchedRegistration.accountId,
              });
              return;
            }
            this.initOnboarding(route.customToken || customToken, urlParams);
          }
        })
        .catch(() => {
          postMessage(MESSAGES_TYPES.ONBOARDING_END_UNSUCCESSFULLY);
          SystemPromptService.showAlert(ALERTS.INVALID_OTL_CODE);
        });
      return;
    }

    this.initOnboarding(this.props.customToken, urlParams);
  }

  initOnboarding(customToken, urlParams = {}, options = {}) {
    const { clinicianRegistrationOnly = false, alreadySignedIn = false } = options;
    const accountTypeHint = resolveAppTypeHintFromUrlParams(urlParams);

    return getFirebaseUser(alreadySignedIn ? null : customToken)
      .then((user) =>
        resolveAccountType(user, accountTypeHint).then((accountType) => {
          if (isClinicianAccountType(accountType)) {
            return bootstrapClinicianRegistration(user.uid, {
              accountType,
              user,
              loginRes: { customToken, accountId: user.uid, accountType },
            })
              .then((bootstrap) => {
                postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
                this.setState({
                  route: "clinician",
                  prefetchedRegistration: bootstrap.wizardAccount,
                  accountId: user.uid,
                });
              })
              .catch(() => {
                postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
                this.setState({
                  route: "clinician",
                  prefetchedLoginRes: {
                    accountId: user.uid,
                    accountType: USER_TYPES.THERAPIST,
                    customToken,
                  },
                  accountId: user.uid,
                });
              });
          }

          if (clinicianRegistrationOnly) {
            throw new Error("registrationCode is only supported for clinicians");
          }

          return user.getIdToken().then((idToken) =>
            ApiService.loginHelper(user.uid, idToken).then((loginRes) => {
              postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
              this.setState({
                route: "patient",
                prefetchedPatientAccount: loginRes?.accountData || null,
                prefetchedLoginRes: withCustomToken(loginRes, customToken),
                accountId: user.uid,
              });
            })
          );
        })
      )
      .catch((error) => {
        if (clinicianRegistrationOnly) {
          throw error;
        }
        this.setState({ route: "patient" });
      });
  }

  render() {
    const { route, prefetchedRegistration, prefetchedLoginRes, prefetchedPatientAccount, accountId } =
      this.state;

    if (!route) {
      return <SpinnerMan />;
    }

    if (route === "clinician") {
      return (
        <ClinicianOnboardingWizardModal
          {...this.props}
          prefetchedRegistration={prefetchedRegistration}
          prefetchedLoginRes={prefetchedLoginRes}
          prefetchedAccountId={accountId}
        />
      );
    }

    return (
      <OnboardingWizardModal
        {...this.props}
        prefetchedAccount={prefetchedPatientAccount}
        prefetchedLoginRes={prefetchedLoginRes}
        prefetchedAccountId={accountId}
      />
    );
  }
}

export default OnboardingRouter;
